import React, {FC, Fragment, MouseEvent as ReactMouseEvent, ReactNode, useEffect, useRef, useState} from "react";
import Script from "next/script"
import {IProduct, IProductOption, ISizeChart, TranslationsType} from "../../../types/homePageTypes";
import {useDispatch, useSelector} from "react-redux";
import {IGlobalProduct, IState} from "../../../types/state";
import {useToast} from "../../../atoms/Toast/useToast";
import {useRouter} from "next/router";
import cls from "./customInnerPage.module.scss";
import {CheckToastSvg, FailSvg, HeartActive, HeartSvg} from "../../../svg";
import {wishlistAddItem, wishlistRemoveItem} from "../../../store/wishlist";
import CustomDetails from "./CustomDetails";
import CustomSideBar from "./CustomSideBar";
import CustomSocialLinks from "./CustomSocialLinks";
import CustomDetailTabButtons from "./CustomDetailTabButtons";
import CustomInfoPart from "./CustomInfoPart";
import {ICurrencies} from "../Wrappers/MainLayout";
import {
    addCartDivStyleToggleConfig,
    configInActiveVariantHelper,
    configNotExistVariantHelper,
    dangerOrSuccessStyleConfig,
    isSelectedDetailSectionClass,
    schemaProduct,
    showBoxAboutDetails, useCustomOptions,
    useFetchCartToken
} from "./helpers";
import CustomConfigurableFilters from "./CustomConfigurableFilters";
import CustomProductGallery from "./CustomProductGallery";
import InputNumber from "../../shared/InputNumber";
import {handleAddCard, openUpCrossProd} from "../../shared/ProductCard/helpers";
import {IRequestFormDetails} from "../../../pages/[productSlug]";
import {IFormComponentType} from "../../builder/types";
import {setNotFilledOptionsAction, setOptionValuesAction} from "../../../store/product";

interface ICustomConfig {
    dbName: string;
    domain: string;
    productSlug: string;
    selectedRate: ICurrencies;
    product: IProduct;
    backOrderValue: string;
    translationsData: TranslationsType;
    selectedLocale: string;
    schemaBrandName: string;
    showDetailsInFront: boolean;
    showBuilderComponents: (id: number) => Record<"true" | "false", ReactNode>;
    showBuilderData: boolean;
    requestFormDetails: IRequestFormDetails;
    requestFormInfo: IFormComponentType | null;
    sizeChart: ISizeChart | null
}

const CustomConfig: FC<ICustomConfig> = (
    {
        dbName,
        productSlug,
        domain,
        selectedRate,
        product,
        backOrderValue,
        translationsData,
        selectedLocale,
        schemaBrandName,
        showDetailsInFront,
        showBuilderComponents,
        showBuilderData,
        requestFormDetails,
        requestFormInfo,
        sizeChart
    }
) => {
    const {
        flats: [{
            name: productName = "",
            description: productDesc = "",
            price_html: productPriceHtml = null,
            min_qty: productMinQtyServer = 1
        } = {}] = [],
        details: productDetails = [],
        images: [{
            path: productImgPath = ""
        } = {}] = [],
        id: productId,
        sku: productSku = "",
        variants: configVariants = [],
        attributes: configAttributes = [],
    } = product
    const clientSideProduct = configVariants?.find(elem => elem.is_selected) || product;
    const productMinQty = !(!!productMinQtyServer) || !(!!+productMinQtyServer) ? 1 : productMinQtyServer;
    const [filtered, setFiltered] = useState<Record<number | string, number | string>>({});
    const [configAttributesCSR, setConfigAttributesCSR] = useState(configAttributes);
    const [change, setChange] = useState(false);
    const [quantity, setQuantity] = useState(productMinQty);
    const [csrProductWithOpts, setCsrProductWithOpts] = useState<IProduct>(clientSideProduct);
    const [wishIcon, setWishIcon] = useState<React.JSX.Element>(<HeartSvg/>)
    const wishlist = useSelector((state: IState) => state.wishlist);
    const isProductInWishList = wishlist?.find((item: IGlobalProduct) => item.id === productId)
    useEffect(() => {
        let removalObject: { [x: number]: any; } | null = null
        if (configVariants.some((elem => elem.flats[0].status === 0 || elem.qty[0] === 0))) {
            configVariants.forEach((variant) => {
                if (variant.flats[0].status === 0 || variant.qty[0] === 0) {
                    const filterOnlyInt = variant.product_attribute_values.filter((prAttr: {integer_value: string, attribute_id: number}) =>
                        prAttr.integer_value && configAttributes.some(iii => iii?.attributes_values[0]?.id === prAttr?.attribute_id)
                    )
                    if (filterOnlyInt.length === 1) {
                        if (removalObject?.[filterOnlyInt[0].attribute_id]) {
                            removalObject = {
                                ...removalObject,
                                [filterOnlyInt[0].attribute_id]: [...removalObject?.[filterOnlyInt[0].attribute_id], filterOnlyInt[0].integer_value]
                            }
                        } else {
                            removalObject = {
                                ...removalObject,
                                [filterOnlyInt[0]?.attribute_id]: [filterOnlyInt[0].integer_value]
                            }
                        }
                    }
                    if (removalObject) {
                        setConfigAttributesCSR(() => {
                            return configAttributes.map((confAttr) => {
                                return {
                                    ...confAttr,
                                    attribute_options: confAttr?.attribute_options?.map((attrOption) => {
                                        if (removalObject?.[confAttr?.attributes_values?.[0]?.id]?.every((iii: string) => String(iii) !== String(attrOption.id))) {
                                            return attrOption
                                        } else {
                                            return {...attrOption, disabled: true}
                                        }
                                    }),
                                    attributes_values: [{
                                        ...confAttr?.attributes_values[0],
                                        attribute_options: confAttr?.attributes_values[0]?.attribute_options.map((attrOption) => {
                                            if (removalObject?.[confAttr?.attributes_values?.[0]?.id]?.every((iii: string) => String(iii) !== String(attrOption.id))) {
                                                return attrOption
                                            } else {
                                                return {...attrOption, disabled: true}
                                            }
                                        })
                                    }]
                                }
                            })
                        })
                    } else {
                        setConfigAttributesCSR(configAttributes);
                    }
                }
            })
        } else {
            setConfigAttributesCSR(configAttributes)
        }
    }, [productId]);
    const {
        flats: [{
            name: clientProductName = "",
            short_description: clientProductShortDesc = "",
            description: clientProductDesc = "",
            min_price: productMinPrice = null,
            special_price: productSpecialPrice = null,
            price: productPrice = null,
        }],
        sku_option: {name: clientProductSkuOptionName = "SKU"} = {},
        sku: clientProductSku = "",
        short_description_option: {name: clientProductShortDescOptionName = ""} = {},
        description_option: {name: clientProductDescOptionName = ""} = {},
        details: clientProductDetails = [],
        product_options = [],
        is_selected: variantProductIsSelected = false,
    } = clientSideProduct
    useEffect(() => {
        setWishIcon(!!isProductInWishList ? <HeartActive className={cls["inner-wishlist"]}/> : <HeartSvg/>)
    }, [isProductInWishList]);


    const [isSelectDetail, setIsSelectDetail] = useState<boolean>(!!clientProductDesc);
    const cartToken = useSelector((state: IState) => state.cartToken);
    const optionValues = useSelector(((state: IState) => state.product.optionValues));
    const {token: customerToken} = useSelector((state: IState) => state.customer);
    const signed = useSelector((state: IState) => state.customer.authenticated);
    const dispatch = useDispatch();
    const toast = useToast();
    const {asPath, push: routerPush} = useRouter();
    const rootRef = useRef<HTMLDivElement>(null)
    const isProductInStock = clientSideProduct.qty[0] > 0 || !!parseFloat(backOrderValue)
    const schema = schemaProduct(asPath, productName, productDesc, productImgPath, productSku, productId, dbName, domain, schemaBrandName);
    const addToast = (error?: boolean) => {
        if (error) {
            toast.open(
                <span className="d-flex chek-fms">
                    <FailSvg/>
                    {translationsData.ProductNotAvailable}
                </span>
            )
        } else {
            if (isProductInStock) {
                toast.open(
                    <span className="d-flex chek-fms">
                        <CheckToastSvg/>
                        {translationsData.ToastAddToCart}
                   </span>
                )
            }
        }
    }
    const seperatedAttrs = configVariants
        .map(({product_attribute_values}: any) => {
                return product_attribute_values.filter((prAttr: {integer_value: string, attribute_id: number}) => prAttr.integer_value)
            })
        .flat()
        .reduce((acc, curr) => {
            return {
                ...acc,
                [curr.product_id]: {
                    ...(acc[curr.product_id]),
                    [curr.attribute_id]: curr.integer_value
                }
            }
    }, {})

    useEffect(() => {
        const data = configVariants
            ?.filter(({product_attribute_values}: {
                product_attribute_values: Array<{ integer_value: string; attribute_id: number }>
            }) => {
                const {length: filteredLength = 0} = product_attribute_values?.filter((
                    {
                        integer_value,
                        attribute_id
                    }
                ) => {
                    if (!integer_value) return false
                    return String(filtered[attribute_id]) === String(integer_value)
                }) || [];
                if (filteredLength > 0) {
                    const {length: filteredValuesLength = 0} = Object.values(filtered)
                    return filteredLength === filteredValuesLength
                }
                return false
            })
        if (data.length === 1 && (Object.keys(filtered).length > 0 || configVariants.length !== 1) && !!Object.values(filtered).length) {
            routerPush(`/${data[0].flats[0].url_key}`)
        } else if (data.length === configVariants.length) {
            routerPush(`/${product.flats[0].url_key}`)
        }

        setIsSelectDetail(!clientProductDesc)

    }, [change, clientProductDesc])

    const handleChangeConfig = ({attribute_id, id}: { attribute_id: number, id: number }) => {
        let updatedFiltered = {...filtered};
        const updateStateHandle: Record<string, () => string | number | boolean> = {
            "true": () => delete updatedFiltered[attribute_id],
            "false": () => updatedFiltered[attribute_id] = id
        }
        updateStateHandle[`${String(updatedFiltered[attribute_id] === String(id))}`]()
        configInActiveVariantHelper(
            {
                configVariants,
                updatedFiltered,
                setConfigAttributesCSR,
                configAttributes,
                configAttributesCSR
            }
        )
        configNotExistVariantHelper(
            {
                configAttributesCSR,
                filtered: updatedFiltered,
                setConfigAttributesCSR,
                seperatedAttrs,
                variantProductIsSelected
            }
        )
        setFiltered(updatedFiltered);
        setChange((prevChange) => !prevChange);
    };
    useEffect(() => {
        const selectedVariant = configVariants?.find(elem => elem.is_selected);
        if (selectedVariant) {
            const attrValues = selectedVariant?.product_attribute_values?.filter(({integer_value}: {integer_value: string}) => integer_value) || [];
            //TODO!!! this part written for more strict matching of attributes and their values, but i dont want at start wrote too complicated logic, so i commented it, after test if we would need it i will uncomment it
            const x = configAttributes.map((elem) => {
                return {[elem?.attributes_values?.[0]?.id]: elem?.attributes_values?.[0]?.attribute_options.map(e => e.id)}
            })
            let insideFiltered: Record<string, number> = {};
            attrValues?.forEach(({attribute_id, integer_value}: { attribute_id: number, integer_value: string }) => {
                const hasThisKey = x.find(elem => Object.keys(elem)[0] === String(attribute_id))
                if (hasThisKey) {
                    if (x.some(elem => Object.keys(elem)[0] === String(attribute_id) && elem[attribute_id].includes(Number(integer_value)))) {
                        insideFiltered = {
                            ...insideFiltered,
                            [attribute_id]: Number(integer_value)
                        }
                        setFiltered((prevState) => ({
                            ...prevState,
                            [attribute_id]: Number(integer_value)
                        }))
                    }
                }
            })
            //TODO!!! this part for simple matching of attributes and their values
            // let insideFiltered: Record<string, number> = {};
            // attrValues?.forEach(({attribute_id, integer_value}: { attribute_id: number, integer_value: string }) => {
            //     insideFiltered = {
            //         ...insideFiltered,
            //         [attribute_id]: Number(integer_value)
            //     }
            //     setFiltered((prevState) => ({
            //         ...prevState,
            //         [attribute_id]: Number(integer_value)
            //     }))
            // })
            configInActiveVariantHelper(
                {
                    configVariants,
                    updatedFiltered: insideFiltered,
                    setConfigAttributesCSR,
                    configAttributes,
                    configAttributesCSR
                }
            )
            configNotExistVariantHelper(
                {
                    configAttributesCSR,
                    filtered: insideFiltered,
                    setConfigAttributesCSR,
                    seperatedAttrs,
                    variantProductIsSelected
                }
            )
        }
    }, []);

    const calculateMaxQty = () => {
        const filteredDataLengthToggle = () => {
            const {qty: [maxVariantQuantity = 0] = []} = configVariants
                ?.find(({qty: [dataElemQty]}: { qty: [number] }) => dataElemQty > 0) || {}
            return maxVariantQuantity
        }
        const maxQtyHandle: Record<string, Record<string, number>> = {
            "true": {
                "true": configVariants[0]?.maxQty,
                "false": configVariants[0]?.maxQty,
            },
            "false": {
                "true": filteredDataLengthToggle(),
                "false": 0
            }
        }
        return maxQtyHandle[`${!!product?.additional?.default_variant_id}`][`${Object.keys(filtered).length > 0}`]
    }

    const { customOptions, refsMap } = useCustomOptions(
        {
            setClientSideProduct: setCsrProductWithOpts,
            product: clientSideProduct,
            productMinPrice,
            productSpecialPrice,
            productPrice,
            selectedRate,
            asPath,
            productSlug
        }
    )
    useEffect(() => {
        setCsrProductWithOpts(clientSideProduct);
        dispatch(setOptionValuesAction({isConfig: true, id: 0, label: "", price: "", optionId: 0}));
    }, [productSlug, clientProductSku]);
    useFetchCartToken(cartToken)

    const selectTab = (e: ReactMouseEvent<HTMLDivElement>) => {
        setIsSelectDetail((e.target as HTMLDivElement).id === "detail")
    }

    const priceHtmlAvailable = {
        "true": clientSideProduct?.flats[0]?.price_html?.map((e: string, i: number) => (
            <span key={i} className={cls["product_price_html"]}>{e}</span>
        )),
        "false": <></>
    }

    const handleWishIconClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        if (!signed) {
            toast.open(
                <span className="d-flex faild-toast-fms">
                    <FailSvg/>
                    {translationsData.ToastSignIn}
                </span>
            )
        } else {
            !!isProductInWishList ?
                dispatch(wishlistRemoveItem(product.id))
                : dispatch(wishlistAddItem(product, selectedLocale))
            const toastChildren = {
                "true": {svg: <FailSvg/>, title: translationsData?.ToastWishListRemove},
                "false": {svg: <CheckToastSvg/>, title: translationsData?.ToastWishList}
            }
            toast.open(
                <span className="d-flex faild-toast-fms">
                    {toastChildren[`${!!isProductInWishList}`].svg}
                    {toastChildren[`${!!isProductInWishList}`].title}
                </span>
            )
        }
    }

    const descriptions = (
        <div className="product-inner-long-description">
            <div style={{paddingLeft: "30px"}}>
                <div dangerouslySetInnerHTML={{__html: clientProductDesc}}/>
            </div>
        </div>
    )

    const details = <CustomDetails
        details={clientProductDetails}
        isSelectDetail={isSelectDetail}
        showDetailsInFront={showDetailsInFront}
        translationsData={translationsData}
        dbName={dbName}
        domain={domain}
    />
    const handleAddCartClickLocal = async (quantity: number = 1) => {
        if (product_options?.length > 0) {
            const filterRequiredOptions = (product_options as Array<IProductOption>).filter((option) => Number(option?.required));
            if (filterRequiredOptions?.length) {
                const everyItemExist = filterRequiredOptions?.filter((item) => {
                    return !optionValues.some((option) => option.id === item.id);
                }).map(item => item.id)
                if (everyItemExist.length > 0) {
                    dispatch(setNotFilledOptionsAction(everyItemExist));
                    return;
                }
            }
        }
        dispatch(setNotFilledOptionsAction([]));
        const addCartFunc = async () => {
            return await handleAddCard({
                openUpCrossProd: () => {
                    return openUpCrossProd(
                        {
                            dispatch,
                            productId,
                            selectedLocale,
                            selectedRate,
                            product: clientSideProduct
                        }
                    )
                },
                dispatch,
                data: {
                    product: clientSideProduct,
                    quantity,
                    cartToken,
                    customer: { token: customerToken },
                    selectedLocale,
                    selectedCurrCode: selectedRate.code,
                    optionsObject: Object.keys(customOptions?.customizable_options).length > 0 ? customOptions : null,
                    addToast
                },
                routerPush,
                isProductInStock,
            })
        }
        if (product?.attribute_family?.code === "Donation") {
            const { resStatus = 200 } = await addCartFunc() || {} as {message: string, resStatus: number};
            if (resStatus === 200) {
                return await routerPush("/checkout")
            }
        } else {
            return await addCartFunc()
        }
    }

    return (
        <Fragment>
            <Script
                id={schema.name}
                type="application/ld+json"
                dangerouslySetInnerHTML={{__html: JSON.stringify(schema)}}
                defer
            />
            <div>
                <div className={cls["product__content"]}>
                    <CustomProductGallery images={clientSideProduct.images} dbName={dbName}/>
                    <div className={cls["product__info"]}>
                        <CustomInfoPart
                            product={clientSideProduct}
                            productName={clientProductName}
                            productSkuOptionName={clientProductSkuOptionName}
                            productSku={clientProductSku}
                            selectedCurrSymbol={selectedRate?.symbol}
                            selectedCurrCode={selectedRate?.code}
                            clientSidePrice={{
                                price: optionValues?.length > 0 ? csrProductWithOpts?.flats[0].price : clientSideProduct.flats[0].price,
                                specialPrice: optionValues?.length > 0 ? csrProductWithOpts?.flats[0]?.special_price : clientSideProduct.flats[0]?.special_price,
                                cost: optionValues?.length > 0 ? csrProductWithOpts?.flats[0].cost : clientSideProduct.flats[0].cost
                            }}
                            priceHtmlAvailable={priceHtmlAvailable[`${!!productPriceHtml}`]}
                            productShortDescOptionName={clientProductShortDescOptionName}
                            productShortDesc={clientProductShortDesc}
                            productOptions={product_options}
                            translationsData={translationsData}
                            refsMap={refsMap}
                            sizeChart={sizeChart}
                            dbName={dbName}
                            domain={domain}
                        >
                            <CustomConfigurableFilters
                                handleChangeConfig={handleChangeConfig}
                                configurableData={configAttributesCSR}
                                filtered={filtered}
                            />
                        </CustomInfoPart>
                        <ul className={cls["product__meta"]}>
                            <div className={`${cls["product__actions-item"]} ${cls["product-inner-quantity"]}`}>
                                <InputNumber
                                    id="product-quantity"
                                    aria-label="Quantity"
                                    className={cls["product__quantity"]}
                                    size="lg"
                                    min={productMinQty}
                                    max={calculateMaxQty()}
                                    value={quantity}
                                    onChange={(quantity: number) => {
                                        if (quantity > calculateMaxQty()) {
                                            setQuantity(calculateMaxQty())
                                        } else {
                                            setQuantity(quantity)
                                        }
                                    }}
                                />
                            </div>
                            <li className={cls["product__meta-availability"]}>
                                <span
                                    className={dangerOrSuccessStyleConfig[`${isProductInStock}`][`${clientSideProduct.type === "configurable"}`].className}
                                    style={{fontSize: "18px"}}>
                                    {translationsData?.[dangerOrSuccessStyleConfig[`${isProductInStock}`][`${clientSideProduct.type === "configurable"}`].IntlKey]}
                                </span>
                            </li>
                        </ul>
                        <div className={cls.sidebar__details}>
                            {!showDetailsInFront && !!productDetails.length && <CustomDetails
                                details={productDetails}
                                isSelectDetail={isSelectDetail}
                                showDetailsInFront={true}
                                translationsData={translationsData}
                                dbName={dbName}
                                domain={domain}
                            />}
                        </div>
                        <CustomSideBar
                            ProductAddToCart_INTL={translationsData?.AddToCartButtonTitle}
                            actionClassName={addCartDivStyleToggleConfig[`${isProductInStock}`][`${clientSideProduct.parent_id || clientSideProduct?.length === 0}`]}
                            handleCardClick={handleAddCartClickLocal}
                            isButtonDisabled={
                                clientSideProduct.type === "configurable"
                                || !(calculateMaxQty() > 0)
                                || (!+backOrderValue && clientSideProduct.qty[0] === 0)
                            }
                            wishListButtonClick={handleWishIconClick}
                            wishIconClicked={wishIcon}
                            inpValQuantity={quantity}
                            requestFormDetails={requestFormDetails}
                            requestFormInfo={requestFormInfo}
                        />
                        <CustomSocialLinks dbName={dbName} domain={domain} productSlug={productSlug} translationsData={translationsData}/>
                    </div>
                </div>
                {showBuilderComponents(2)[`${showBuilderData}`]}
                <CustomDetailTabButtons
                    boxAboutDetailsClass={showBoxAboutDetails[`${showDetailsInFront}`]}
                    isSelectedDetailSectionClass={isSelectedDetailSectionClass(descriptions, details)[`${isSelectDetail}`]}
                    productDescOptionName={clientProductDescOptionName}
                    Details_INTL={translationsData?.Details}
                    selectTab={selectTab}
                    showDescription={!!clientProductDesc}
                    showDetailsInFront={showDetailsInFront}
                />
                {isSelectedDetailSectionClass(descriptions, details)[`${isSelectDetail}`].content}
            </div>
        </Fragment>
    )
};

export default CustomConfig;

//TODO!!! stay with this if the handler not help setted on click
// useEffect(() => {
//     const filteredVal = Object.entries(lll).filter((elem) => {
//         return Object.entries(filtered).every((asd) => {
//             return String(elem[1][asd[0]]) === String(asd[1])
//         })
//     })
//     const removalObject = Object.values(Object.assign(Object.fromEntries(filteredVal)));
//     setConfigAttributesCSR((prevState) => {
//         return prevState.map((confAttr) => {
//             return {
//                 ...confAttr,
//                 attribute_options: confAttr?.attribute_options?.map((attrOption) => {
//                     const findII = removalObject.some((elem) => {
//                         const uuu = Object.entries(filtered).some((ifff, _, array) => {
//                             const filteredArr = array.filter(asd => String(asd[0]) !== String(ifff[0]));
//                             const x123123123 = Object.values(lll).filter((elem) => {
//                                 return filteredArr.every((asd) => {
//                                     return String(elem[asd[0]]) === String(asd[1])
//                                 })
//                             }).map(oji => {
//                                 const uppOji = Object.entries(oji).filter(asd => {
//                                     return !filteredArr.some(asdasd => asdasd[0] === asd[0])
//                                 })
//                                 return Object.fromEntries(uppOji)
//                             })
//                             return x123123123.some((elemelem) => String(elemelem[attrOption.attribute_id]) === String(attrOption.id))
//                         })
//                         if (Object.values(filtered).length === configAttributesCSR.length || variantProductIsSelected) {
//                             return uuu
//                         }
//                         if (
//                             filtered.hasOwnProperty(attrOption.attribute_id)
//                             && Object.values(filtered).length !== configAttributesCSR.length - 1
//                         ) {
//                             return true
//                         }
//                         return String(elem[attrOption.attribute_id]) === String(attrOption.id)
//                     })
//                     if (findII) {
//                         return {...attrOption, disabled: false}
//                     } else {
//                         return {...attrOption, disabled: true}
//                     }
//                 }),
//                 attributes_values: [{
//                     ...confAttr?.attributes_values[0],
//                     attribute_options: confAttr?.attributes_values[0]?.attribute_options.map((attrOption) => {
//                         const findII = removalObject.some((elem) => {
//                             const uuu = Object.entries(filtered).some((ifff, _, array) => {
//                                 const filteredArr = array.filter(asd => String(asd[0]) !== String(ifff[0]));
//                                 const x123123123 = Object.values(lll).filter((elem) => {
//                                     return filteredArr.every((asd) => {
//                                         return String(elem[asd[0]]) === String(asd[1])
//                                     })
//                                 }).map(oji => {
//                                     const uppOji = Object.entries(oji).filter(asd => {
//                                         return !filteredArr.some(asdasd => asdasd[0] === asd[0])
//                                     })
//                                     return Object.fromEntries(uppOji)
//                                 })
//                                 return x123123123.some((elemelem) => String(elemelem[attrOption.attribute_id]) === String(attrOption.id))
//                             })
//                             if (Object.values(filtered).length === configAttributesCSR.length || variantProductIsSelected) {
//                                 return uuu
//                             }
//                             if (
//                                 filtered.hasOwnProperty(attrOption.attribute_id)
//                                 && Object.values(filtered).length !== configAttributesCSR.length - 1
//                             ) {
//                                 return true
//                             }
//                             return String(elem[attrOption.attribute_id]) === String(attrOption.id)
//                         })
//                         if (findII) {
//                             return {...attrOption, disabled: false}
//                         } else {
//                             return {...attrOption, disabled: true}
//                         }
//                     })
//                 }]
//             }
//         })
//     })
// }, [filtered]);